<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "What moved, and how much?"
  Answer:   the effects column - the amount, largest weight in a row, tabular figures, split
            into an integer and a fraction track so every row's decimal point sits at one fixed
            offset; the currency code sits in its own narrow, quiet track so it never shifts
            where the digits land. Colour marks the effect's kind (income/expense/transfer),
            never the sign of the number - a negative amount is read off its own minus glyph.
            A mixed entry stacks one figure per kind and currency under the same top weight.
            A figure never wraps or truncates - breaking a number mid-digit-group reads as two
            numbers, worse than either alternative - so past a threshold of integer digits the
            whole amount steps down one type-scale step instead, staying on one line at full
            precision. The page is capped at 70rem (1120px) and carries a four-column table
            (date, entry, summary, effects): the entry column carries a max-w-72 (18rem) so an
            unconstrained payee name cannot starve the money column it sits beside (see
            TransactionRowWide.vue). A numeric(19,4) boundary figure is the one case still
            expected to need the wrapper's horizontal scroll - the same never-truncate rule that
            steps a figure's size down keeps it from ever wrapping or compressing there either.
            That wrapper carries its own tabindex and label so the scroll stays
            keyboard-reachable (WCAG 2.1.1).
  Context:  date, entry title/memo, and summary (account to category) - regular
            weight, muted, smaller, never compete with the amount's size or weight. The row
            centres vertically (items-center) rather than aligning to the top, because the
            amount is always a single line.
  Controls: "Add entries" is the one --color-action control on the screen - the only thing
            here that creates rather than reads. "Load more" and "Try again" stay a neutral,
            secondary tier (pagination and recovering from a failed load), and so does the
            "Group by day" toggle in "More actions": it changes how the list reads, not what exists,
            and its state lives in the URL's `grouping` query param so a shared link restores the same
            view (grouped is the default; `grouping=flat` is the only other value).
            "Exports" is a tool over this screen's own data (AGENTS.md, "The membership
            rule"), gated on the same predicate as the journal itself, so it is reached from
            here rather than advertised as its own nav destination.
  Detail:   opening a row's entry detail is a centred modal dialog (Dialog, EntryDetailDialog.vue),
            not a route change. The open entry lives in the `entry` query param, so the dialog
            survives a reload; closing it (Escape, outside click, Close) only drops that param,
            and the list's scroll position is never touched. While an edit has unsaved changes,
            Escape, Close, backdrop and in-dialog navigation ask "Discard changes?" first, inside
            the dialog; reload and tab close use the browser's beforeunload prompt; browser Back
            and Forward are not guarded.
-->
<script setup lang="ts">
import {
  ActionButton,
  Dialog,
  IconButton,
  MenuItem,
  MenuPopover,
  MenuSwitchItem,
  PageHeader,
  PageShell,
} from '@taler/ui'
import type { LocalDate } from '@taler/domain'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { canCreateEntries, useEntryMerchants, useEntryTags } from '@/features/entries'
import { canViewExports } from '@/features/exports'
import { canManageReceiptScans } from '@/features/receipt-scans'
import { canManageImports } from '@/features/imports'
import { canViewMerchants } from '@/features/merchants'
import {
  canDeleteEntry,
  canEditEntry,
  canReverseEntry,
  canViewTransactions,
  type CrossLedgerLink,
  EntryDetailDialog,
  type EntryDraft,
  postableAccountOptions,
  TransactionFilters,
  TransactionList,
  TransactionSearch,
  toEntryDetailView,
  useEntryDetail,
  useTransactionFilters,
  useTransactions,
} from '@/features/transactions'
import {
  canScheduleRecognitions,
  isPrepaidEligibleOrigin,
  PrepaidScheduleSection,
  usePrepaidSchedule,
} from '@/features/prepaid-recognitions'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()
const route = useRoute()
const router = useRouter()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const canViewEntries = computed(() => canViewTransactions(session.can))
const canAddEntries = computed(() => canCreateEntries(session.can))
const canExport = computed(() => canViewExports(session.can))
const canScanReceipts = computed(() => canManageReceiptScans(session.can))
const canImport = computed(() => canManageImports(session.can))
const canViewMerchantDirectory = computed(() => canViewMerchants(session.can))
const canEdit = computed(() => canEditEntry(session.can))
const canReverse = computed(() => canReverseEntry(session.can))
const canDelete = computed(() => canDeleteEntry(session.can))
const canSchedulePrepaid = computed(() => canScheduleRecognitions(session.can))
const grouped = computed(() => route.query.grouping !== 'flat')
const openEntryId = computed(() => (typeof route.query.entry === 'string' ? route.query.entry : undefined))
const isEditing = ref(false)
/** Set by the row menu's Edit so the entry it opens arrives in edit mode. The watch below is the
 * only writer of `isEditing` on a navigation, so the two cannot race. */
const pendingEdit = ref<string>()
watch(openEntryId, (id) => {
  isEditing.value = id !== undefined && id === pendingEdit.value
  pendingEdit.value = undefined
})
const entryEditor = ref<InstanceType<typeof EntryDetailDialog>>()

function toggleGrouping(): void {
  const query = { ...route.query, grouping: grouped.value ? 'flat' : undefined }
  void router.replace({ query })
}

const searchInput = useTemplateRef<InstanceType<typeof TransactionSearch>>('searchInput')
function clearFilters(): void {
  searchInput.value?.reset()
  transactionFilters.clearAll()
}

function guardedLeave(proceed: () => void): void {
  if (entryEditor.value) entryEditor.value.requestLeave(proceed)
  else proceed()
}

function openEntry(id: string): void {
  guardedLeave(() => void router.push({ query: { ...route.query, entry: id } }))
}

function closeEntry(): void {
  void router.push({ query: { ...route.query, entry: undefined } })
}

/** Switching ledger is global session state, not a route segment - the query change alone
 * re-resolves the entry detail and the list once the new ledger's data is in. */
function followCrossLedgerLink(link: CrossLedgerLink): void {
  guardedLeave(() => {
    session.selectLedger(link.counterpartLedgerId)
    void router.push({ query: { ...route.query, entry: link.counterpartEntryId } })
  })
}

/** The row menu's Edit opens the detail already in edit mode; its Reverse opens the detail,
 * where the reverse control states its consequence, rather than reversing from the row. */
function editEntry(id: string): void {
  guardedLeave(() => {
    if (openEntryId.value === id) {
      isEditing.value = true
      return
    }
    pendingEdit.value = id
    void router.push({ query: { ...route.query, entry: id } }).then((failure) => {
      if (failure !== undefined) pendingEdit.value = undefined
    })
  })
}

const transactionFilters = useTransactionFilters()

const transactions = useTransactions({
  ledgerId: () => session.activeLedger.value?.id,
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => canViewEntries.value,
  filters: () => transactionFilters.filters.value,
})

const listVisible = computed(
  () => !session.isLoading.value && session.activeLedger.value !== undefined && canViewEntries.value,
)

const hasOverflowActions = computed(
  () =>
    canViewEntries.value ||
    (canAddEntries.value && transactions.error.value === null) ||
    (canImport.value && transactions.error.value === null) ||
    canViewMerchantDirectory.value ||
    canExport.value,
)

const filterAccountOptions = computed(() =>
  postableAccountOptions(transactions.accountTree.value, currentLocale.value),
)

const merchantSearchQuery = ref('')
const filterMerchants = useEntryMerchants({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canViewMerchantDirectory.value,
  search: () => merchantSearchQuery.value,
})

const filterTags = useEntryTags({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canViewEntries.value,
})

const entryDetail = useEntryDetail({
  ledgerId: () => session.activeLedger.value?.id,
  entryId: () => openEntryId.value,
  enabled: () => openEntryId.value !== undefined,
})

const { displayCurrency } = useDisplayCurrency()
const entryView = computed(() =>
  entryDetail.entry.value === undefined
    ? undefined
    : toEntryDetailView(
        entryDetail.entry.value,
        entryDetail.accounts.value,
        currentLocale.value,
        displayCurrency.value,
      ),
)
const accountOptions = computed(() =>
  postableAccountOptions(entryDetail.accountTree.value, currentLocale.value),
)

// A schedule is only ever offered on an entry shaped like a prepayment - a transfer with
// exactly two postings, one into an asset account - so the form is never shown on an
// ordinary expense/income entry it is guaranteed to reject.
const prepaidEligibleOrigin = computed(
  () =>
    entryDetail.entry.value !== undefined &&
    isPrepaidEligibleOrigin(entryDetail.entry.value, entryDetail.accountTree.value),
)

const prepaidSchedule = usePrepaidSchedule({
  ledgerId: () => session.activeLedger.value?.id,
  originEntryId: () => openEntryId.value,
  enabled: () => openEntryId.value !== undefined && canSchedulePrepaid.value && prepaidEligibleOrigin.value,
  locale: () => currentLocale.value,
})

async function schedulePrepaidRecognition(input: {
  expenseAccountId: string
  periods: number
  startDate: LocalDate
}): Promise<void> {
  try {
    await prepaidSchedule.scheduleRecognition(input)
  } catch {
    // surfaced through prepaidSchedule.scheduleErrorDetail/scheduleErrorPending
  }
}

async function saveEntry(draft: EntryDraft): Promise<void> {
  try {
    await entryDetail.replace(draft)
    isEditing.value = false
  } catch {
    // surfaced through entryDetail.save.errorDetail/errorPending
  }
}

async function reverseEntry(): Promise<void> {
  try {
    await entryDetail.reverseEntry()
  } catch {
    // surfaced through entryDetail.reverse.errorDetail/errorPending
  }
}

async function deleteEntry(): Promise<void> {
  try {
    await entryDetail.deleteEntry()
    closeEntry()
  } catch {
    // surfaced through entryDetail.remove.errorDetail/errorPending
  }
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('transactions.title')">
      <template #subtitle>{{ t('transactions.subtitle') }}</template>
      <template #actions>
        <TransactionSearch
          v-if="listVisible"
          ref="searchInput"
          :search="transactionFilters.filters.value.search"
          @filter="transactionFilters.setFilter($event)"
        />
        <RouterLink
          v-if="canScanReceipts && transactions.error.value === null"
          :to="{ name: 'receipt-scan' }"
          class="inline-flex min-h-[var(--density-control)] items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)]"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            class="shrink-0 text-[color:var(--color-text-muted)]"
          >
            <path d="M3.6 2.2h8.8v11.6l-2.2-1.2-2.2 1.2-2.2-1.2-2.2 1.2zM6 5.6h4M6 8.4h4" />
          </svg>
          {{ t('transactions.scanReceipt') }}
        </RouterLink>
        <ActionButton
          :as="RouterLink"
          v-if="canAddEntries && transactions.error.value === null"
          :to="{ name: 'entries-new' }"
          class="text-[length:var(--text-step-3)] font-semibold"
        >
          {{ t('entries.title') }}
        </ActionButton>
      </template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('transactions.noLedger') }}
    </p>

    <p v-else-if="!canViewEntries" class="text-[color:var(--color-text-muted)]">
      {{ t('transactions.noAccess') }}
    </p>

    <template v-else>
      <div class="flex items-start gap-[var(--space-inline)]">
        <TransactionFilters
          class="min-w-0 flex-1"
          :filters="transactionFilters.filters.value"
          :account-options="filterAccountOptions"
          :can-view-merchants="canViewMerchantDirectory"
          :merchants="filterMerchants.merchants.value"
          :merchant-frecency="filterMerchants.frecency.value"
          :tag-options="filterTags.tags.value"
          @filter="transactionFilters.setFilter($event)"
          @clear="clearFilters"
          @merchant-search="merchantSearchQuery = $event"
        />
        <MenuPopover v-if="hasOverflowActions" side="bottom" align="end">
          <template #trigger>
            <IconButton variant="bordered" :aria-label="t('transactions.moreActions')">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                <circle cx="3.5" cy="8" r="1.4" />
                <circle cx="8" cy="8" r="1.4" />
                <circle cx="12.5" cy="8" r="1.4" />
              </svg>
            </IconButton>
          </template>
          <MenuSwitchItem
            v-if="canViewEntries"
            :model-value="grouped"
            :label="t('transactions.groupByDay')"
            :description="t('transactions.groupByDayHint')"
            @update:model-value="toggleGrouping"
          />
          <MenuItem v-if="canAddEntries && transactions.error.value === null" as-child>
            <RouterLink :to="{ name: 'entries-split' }">{{ t('transactions.splitExpense') }}</RouterLink>
          </MenuItem>
          <MenuItem v-if="canImport && transactions.error.value === null" as-child>
            <RouterLink :to="{ name: 'imports' }">{{ t('transactions.importCsv') }}</RouterLink>
          </MenuItem>
          <MenuItem v-if="canViewMerchantDirectory" as-child>
            <RouterLink :to="{ name: 'merchants' }">{{ t('merchants.title') }}</RouterLink>
          </MenuItem>
          <MenuItem v-if="canExport" as-child>
            <RouterLink :to="{ name: 'exports' }">{{ t('exports.title') }}</RouterLink>
          </MenuItem>
        </MenuPopover>
      </div>

      <TransactionList
        :rows="transactions.rows.value"
        :cross-ledger-links="transactions.crossLedgerLinks.value"
        :is-loading="transactions.isLoading.value"
        :is-fetching-next-page="transactions.isFetchingNextPage.value"
        :has-next-page="transactions.hasNextPage.value"
        :error="transactions.error.value"
        :grouped="grouped"
        :filters-active="transactionFilters.activeCount.value > 0"
        :can-edit="canEdit"
        :can-reverse="canReverse"
        @load-more="transactions.fetchNextPage()"
        :retrying="transactions.retrying.value"
        @retry="transactions.retry()"
        @edit="editEntry"
        @reverse="openEntry"
        @follow-cross-ledger-link="followCrossLedgerLink"
      />
    </template>

    <Dialog
      :open="openEntryId !== undefined"
      :label="t('transactions.detail.label')"
      @close="guardedLeave(closeEntry)"
    >
      <EntryDetailDialog
        ref="entryEditor"
        :entry="entryView"
        :cross-ledger-link="entryDetail.crossLedgerLink.value"
        :posting-account-options="accountOptions"
        :is-loading="entryDetail.isLoading.value"
        :error="entryDetail.error.value"
        :can-edit="canEdit"
        :can-reverse="canReverse"
        :can-delete="canDelete"
        :is-editing="isEditing"
        :is-saving="entryDetail.save.isPending.value"
        :save-error-detail="entryDetail.save.errorDetail.value"
        :save-error-pending="entryDetail.save.errorPending.value"
        :is-reversing="entryDetail.reverse.isPending.value"
        :reverse-error-detail="entryDetail.reverse.errorDetail.value"
        :reverse-error-pending="entryDetail.reverse.errorPending.value"
        :is-deleting="entryDetail.remove.isPending.value"
        :delete-error-detail="entryDetail.remove.errorDetail.value"
        :delete-error-pending="entryDetail.remove.errorPending.value"
        @close="closeEntry"
        :retrying="entryDetail.retrying.value"
        @retry="entryDetail.retry()"
        @edit="isEditing = true"
        @cancel-edit="isEditing = false"
        @save="saveEntry"
        @reverse="reverseEntry"
        @delete="deleteEntry"
        @follow-cross-ledger-link="followCrossLedgerLink"
      />

      <PrepaidScheduleSection
        v-if="canSchedulePrepaid && entryView !== undefined && prepaidEligibleOrigin"
        class="border-t border-[color:var(--color-border)] p-[var(--space-block)]"
        :origin-entry-date="entryView.date"
        :schedule="prepaidSchedule.schedule.value"
        :not-scheduled="prepaidSchedule.notScheduled.value"
        :is-loading="prepaidSchedule.isLoading.value"
        :error="prepaidSchedule.error.value"
        :retrying="prepaidSchedule.retrying.value"
        :expense-account-options="prepaidSchedule.expenseAccountOptions.value"
        :is-scheduling="prepaidSchedule.isScheduling.value"
        :schedule-error-detail="prepaidSchedule.scheduleErrorDetail.value"
        :schedule-error-pending="prepaidSchedule.scheduleErrorPending.value"
        @retry="prepaidSchedule.retry()"
        @schedule="schedulePrepaidRecognition"
        @open-entry="openEntry"
      />
    </Dialog>
  </PageShell>
</template>
